import React, { useEffect, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { getFirebaseAuth } from './lib/firebase';
import { getUserData } from './lib/firebase/users';
import { useAuthStore } from './store/authStore';
import { Navbar } from './components/layout/Navbar';
import { Footer } from './components/layout/Footer';
import { ScrollToTop } from './components/ScrollToTop';
import { CookieConsent } from './components/CookieConsent';

// Eager load Resources to prevent loading issues
import Resources from './pages/Resources';

// Lazy load other pages
const Home = React.lazy(() => import('./pages/Home'));
const Services = React.lazy(() => import('./pages/Services'));
const About = React.lazy(() => import('./pages/About'));
const AdminDashboard = React.lazy(() => import('./pages/AdminDashboard'));
const Contact = React.lazy(() => import('./pages/Contact'));
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const Auth = React.lazy(() => import('./pages/Auth'));
const Privacy = React.lazy(() => import('./pages/Privacy'));
const Terms = React.lazy(() => import('./pages/Terms'));
const FAQ = React.lazy(() => import('./pages/FAQ'));
const NotFound = React.lazy(() => import('./pages/NotFound'));

const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-[60vh]">
    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
  </div>
);

function App() {
  const { setUser, setUserData, setLoading } = useAuthStore();

  useEffect(() => {
    let isMounted = true;
    let unsubscribe = () => undefined;

    setLoading(true);

    void (async () => {
      try {
        const auth = await getFirebaseAuth();

        if (!isMounted) {
          return;
        }

        unsubscribe = onAuthStateChanged(auth, async (user) => {
          setUser(user);
          if (user) {
            const userData = await getUserData(user.uid);
            setUserData(userData);
          } else {
            setUserData(null);
          }
          setLoading(false);
        });
      } catch (error) {
        console.error('Error initializing Firebase auth:', error);
        if (isMounted) {
          setUser(null);
          setUserData(null);
          setLoading(false);
        }
      }
    })();

    return () => {
      isMounted = false;
      unsubscribe();
      setLoading(false);
    };
  }, [setUser, setUserData, setLoading]);

  return (
    <Router>
      <ScrollToTop />
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-grow">
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/services" element={<Services />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/resources" element={<Resources />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </main>
        <Footer />
      </div>
      <CookieConsent />
    </Router>
  );
}

export default App;
